import React, { useEffect, useState, useContext } from 'react';
import * as AppPics from 'assets/images';
import { AppTextField, AppButton, AppUploadBlock, AppTextArea } from 'components';
import Joi from 'joi';
import { useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { notifier } from 'components/Toaster';
import { useQueryClient } from 'react-query';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import useReplyToDoubt from 'hooks/API/POST/useReplyToDoubt';
import { UserContext } from 'context/UserContext';
import { checkForErrors } from 'utilities/fns';
import useDoubt from 'hooks/API/GET/useDoubt';

function ManageAnswerDoubt() {
  const { doubtId } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { userInfo } = useContext(UserContext) || {};

  const [answerInfo, setAnswerInfo] = useState({
    question: '',
    answer: '',
    answerImage: '',
    answerImageFullPath: '',
  });

  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [deletedImage, setDeletedImage] = useState(false);

  const { data: singleDoubtData } = useDoubt({ doubtId });
  const { mutate: replyToDoubt, isLoading } = useReplyToDoubt();

  useEffect(() => {
    if (singleDoubtData?.data) {
      const d = singleDoubtData.data;

      setAnswerInfo({
        question: d.question || '',
        answer: d.answer || '',
        answerImage: d.answerImage || '',
        answerImageFullPath: d.answerImage || '',
      });

      setDeletedImage(false);
    }
  }, [singleDoubtData]);

  const handleSave = () => {
    setFormErrors([]);
    setValidationErrorServer({});

    if (disableSubmit) return;

    const validation = doubtSchema.validate(
      { answer: answerInfo.answer.trim() },
      { abortEarly: false }
    );

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    const payload = {
      answer: answerInfo.answer.trim(),
      discussionId: doubtId,
      image: deletedImage ? null : answerInfo.answerImage,
      deleteAnswerImage: deletedImage,
    };

    replyToDoubt(payload, {
      onError: (failData) => {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message || 'Failed to submit answer';
        const validationErrors = errorData?.errors || {};
        setValidationErrorServer(validationErrors);
        notifier.error(errorMessage);
      },
      onSuccess: (res) => {
        notifier.success(res?.data?.message || 'Doubt answered successfully!');
        queryClient.invalidateQueries(['doubts']);
        setTimeout(() => history.goBack(), 2000);
      },
    });
  };

  const handleImageChange = ({ uploadedData }) => {
    setAnswerInfo((prev) => ({
      ...prev,
      answerImage: uploadedData.file,
      answerImageFullPath: uploadedData.basePath + uploadedData.file,
    }));
    setDeletedImage(false);
  };

  const handleDeleteImage = () => {
    setAnswerInfo((prev) => ({
      ...prev,
      answerImage: null,
      answerImageFullPath: null,
    }));

    setDeletedImage(true);
    notifier.success('Image deleted successfully');
  };

  const errors = checkForErrors(formErrors);

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <ul className="tab__menu">
          <li className="back">
            <button className="btn-back" onClick={() => history.goBack()}>
              <img src={AppPics.BackSvg} alt="Back" />
            </button>
          </li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[
                { label: 'Dashboard', to: '/dashboard' },
                { label: 'Doubt Clearance', to: '/doubtclearance' },
              ]}
              current={'Answer Doubt'}
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="row">
                <div className="col-6">
                  <h2 className="prime-head p-0 boldtitle">Answer Doubt</h2>

                  {validationErrorServer && (
                    <ul className="error-list">
                      {Object.values(validationErrorServer).map((err, idx) => (
                        <li key={idx}>{err.msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-lg-12">
                  <AppTextField
                    labelProps={{ label: 'Question', htmlFor: 'question' }}
                    inputProps={{
                      id: 'question',
                      name: 'question',
                      value: answerInfo.question,
                      disabled: true,
                    }}
                  />
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-lg-12 col-md-10 col-sm-12">
                  <AppTextArea
                    label="Answer *"
                    areaProps={{
                      placeholder: 'Write your answer...',
                      value: answerInfo.answer,
                      onChange: (e) =>
                        setAnswerInfo((prev) => ({ ...prev, answer: e.target.value })),
                      rows: 10,
                      autoFocus: true,
                    }}
                    fieldErrors={errors.answer}
                  />
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <AppUploadBlock
                    labelProps={{ label: 'Attach Image (optional)' }}
                    props={{
                      referenceKey: 'DOUBT_IMAGES',
                      isImage: true,
                      isPublic: true,
                      uploadCallback: setDisableSubmit,
                      handleChange: handleImageChange,
                      fileData: {
                        fullPath: deletedImage ? null : answerInfo.answerImageFullPath,
                      },
                      clearFile: deletedImage,
                    }}
                  />

                  {answerInfo.answerImageFullPath && !deletedImage && (
                    <button
                      className="actions-icons mx-2 text-danger mt-2"
                      title="Delete Image"
                      onClick={handleDeleteImage}
                      type="button"
                    >
                      <img src={AppPics.DeleteSvg} alt="Delete" />
                    </button>
                  )}
                </div>
              </div>

              <div className="row mt-4 d-flex justify-content-end">
                <AppButton
                  variantClass="btn-primary mx-3"
                  onClick={handleSave}
                  disabled={disableSubmit || isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit Answer'}
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const doubtSchema = Joi.object({
  answer: Joi.string().min(3).max(1000).required().messages({
    'string.empty': 'Answer cannot be empty.',
    'string.min': 'Answer should be at least 3 characters long.',
    'string.max': 'Answer should be at most 1000 characters.',
    'any.required': 'Answer is required.',
  }),
});

export default ManageAnswerDoubt;
